// RequestStatusCertificate.tsx — Sprint 133.1
//
// Public route /request-status-certificate?condo=<uuid> . An EXTERNAL requestor
// (a buyer's solicitor or prospective purchaser) requests a status certificate
// for a unit WITHOUT a login. Wired to POST /api/public/status-certificate-requests
// via the api client with skipAuth. The backend always answers 202 with a
// uniform envelope and never leaks tenant data, so the UI stays deliberately
// neutral: a generic success confirmation, a generic error message, and no
// account is ever required. Modeled on the Sprint-124 public SetPassword page.
import React, { useMemo, useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography, Result } from 'antd';
import { UserOutlined, MailOutlined, HomeOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { atriumTokens } from '../../../../theme/theme';

const { Title } = Typography;
const { TextArea } = Input;

interface RequestValues {
  requestorName: string;
  requestorEmail: string;
  unitNumber: string;
  purpose?: string;
}

export const RequestStatusCertificate: React.FC = () => {
  const [form] = Form.useForm<RequestValues>();
  const [searchParams] = useSearchParams();
  const condoId = useMemo(() => searchParams.get('condo') ?? '', [searchParams]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const focusRing = `0 0 0 3px ${atriumTokens.blueS}, 0 0 0 1px ${atriumTokens.blue}`;
  const addFocusRing = (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.outline = `2px solid ${atriumTokens.blue}`;
    e.currentTarget.style.outlineOffset = '2px';
    e.currentTarget.style.boxShadow = focusRing;
  };
  const clearFocusRing = (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.boxShadow = '';
    e.currentTarget.style.outline = '';
  };

  const handleSubmit = async (values: RequestValues) => {
    setError(null);
    if (!condoId) {
      setError('This link is invalid or incomplete.');
      return;
    }
    setSubmitting(true);
    try {
      await apiService.createPublicStatusCertRequest({
        condoId,
        requestorName: values.requestorName?.trim(),
        requestorEmail: values.requestorEmail?.trim(),
        unitNumber: values.unitNumber?.trim(),
        purpose: values.purpose?.trim() || undefined,
      });
      setSubmitted(true);
    } catch {
      // Keep the message generic — never leak backend detail to a public caller.
      setError('We could not submit your request right now. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: atriumTokens.bg, fontFamily: atriumTokens.fontFamily, padding: '48px 16px',
    }}>
      <Card
        styles={{ body: { padding: 0 } }}
        style={{
          maxWidth: '480px', width: '100%', background: atriumTokens.card,
          boxShadow: atriumTokens.sh2, borderRadius: `${atriumTokens.radiusCard}px`,
          border: `1px solid ${atriumTokens.line}`, fontFamily: atriumTokens.fontFamily, overflow: 'hidden',
        }}
      >
        <div style={{ height: '4px', background: atriumTokens.gradGreeting }} />
        <div style={{ padding: '28px 28px 24px' }}>
          {submitted ? (
            <Result
              status="success"
              title="Request received"
              subTitle="The property manager will follow up with you directly. You do not need a login."
              style={{ padding: '8px 0' }}
            />
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Title level={1} style={{ color: atriumTokens.ink, margin: 0, fontSize: '28px', fontFamily: atriumTokens.fontFamily }}>
                  Request a Status Certificate
                </Title>
                <p style={{ color: atriumTokens.mut, marginTop: 8, fontSize: 14 }}>
                  For a buyer&apos;s solicitor or prospective purchaser. Submit the details
                  below and the property manager will follow up — no account required.
                </p>
              </div>

              {!condoId && (
                <Alert
                  message="This link is invalid or incomplete"
                  description="Please check that you used the full link provided by the property manager."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              {error && (
                <Alert
                  message="Please check the details"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                  closable
                  onClose={() => setError(null)}
                />
              )}

              <Form form={form} name="request-status-certificate" onFinish={handleSubmit} layout="vertical" requiredMark={false}>
                <Form.Item
                  name="requestorName"
                  label="Your name"
                  rules={[{ required: true, message: 'Please enter your name.' }]}
                  style={{ marginBottom: '20px' }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: atriumTokens.mut }} />}
                    placeholder="Full name"
                    size="large"
                    id="requestor-name"
                    autoComplete="name"
                    disabled={!condoId}
                    onFocus={addFocusRing}
                    onBlur={clearFocusRing}
                    style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="requestorEmail"
                  label="Your email"
                  rules={[
                    { required: true, message: 'Please enter your email.' },
                    { type: 'email', message: 'Please enter a valid email address.' },
                  ]}
                  style={{ marginBottom: '20px' }}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: atriumTokens.mut }} />}
                    placeholder="you@example.com"
                    size="large"
                    id="requestor-email"
                    type="email"
                    autoComplete="email"
                    disabled={!condoId}
                    onFocus={addFocusRing}
                    onBlur={clearFocusRing}
                    style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="unitNumber"
                  label="Unit number"
                  rules={[{ required: true, message: 'Please enter the unit number.' }]}
                  style={{ marginBottom: '20px' }}
                >
                  <Input
                    prefix={<HomeOutlined style={{ color: atriumTokens.mut }} />}
                    placeholder="e.g. 1204"
                    size="large"
                    id="unit-number"
                    disabled={!condoId}
                    onFocus={addFocusRing}
                    onBlur={clearFocusRing}
                    style={{ borderRadius: `${atriumTokens.radiusControl}px`, height: '48px', fontSize: '16px' }}
                  />
                </Form.Item>

                <Form.Item
                  name="purpose"
                  label="Purpose (optional)"
                  style={{ marginBottom: '24px' }}
                >
                  <TextArea
                    placeholder="e.g. Pending sale — closing on the 30th."
                    id="purpose"
                    rows={3}
                    disabled={!condoId}
                    onFocus={addFocusRing}
                    onBlur={clearFocusRing}
                    style={{ borderRadius: `${atriumTokens.radiusControl}px`, fontSize: '16px' }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary" htmlType="submit" loading={submitting} size="large" block
                    disabled={!condoId}
                    style={{
                      height: '48px', borderRadius: `${atriumTokens.radiusControl}px`, fontSize: '16px',
                      fontWeight: 600, color: atriumTokens.card, background: atriumTokens.blue, border: 'none',
                      boxShadow: atriumTokens.shc,
                    }}
                  >
                    Submit request
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </div>
      </Card>
    </main>
  );
};

export default RequestStatusCertificate;
